import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  MenuItem,
  AppBar,
  Toolbar,
  Avatar,
  Badge,
  Tabs,
  Tab,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  ArrowBack,
  Visibility,
  Delete,
  Notifications,
  Logout,
  FilterList,
  Search,
  Menu as MenuIcon,
  Circle,
  CheckCircle,
} from '@mui/icons-material';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { getMyRequests, getAllRequests, RequestStatus, deleteRequest } from "../services/dataServiceWrapper";
import { checkIfUserIsApprover } from '../services/graphService';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead, cleanupOldNotifications } from '../services/notificationService';

const statusColors = {
  [RequestStatus.PENDING]: { color: '#f57c00', bg: '#fff3e0' },
  [RequestStatus.IN_PROGRESS]: { color: '#0288d1', bg: '#e1f5fe' },
  [RequestStatus.APPROVED]: { color: '#388e3c', bg: '#e8f5e9' },
  [RequestStatus.REJECTED]: { color: '#d32f2f', bg: '#ffebee' },
  [RequestStatus.COMPLETED]: { color: '#5e35b1', bg: '#ede7f6' },
  [RequestStatus.HOLD]: { color: '#ff6f00', bg: '#fff3e0' },
};

const formTypeNames = {
  leave: 'Leave Request',
  travel: 'Travel Request',
  marketing: 'Marketing Request',
  reimbursement: 'Reimbursement Request',
  extensions: 'Extensions Request',
  wfh: 'WFH Checklist',
  academic: 'Academic Changes',
};

const MyRequests = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accounts, instance } = useMsal();
  const account = accounts[0];
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [isApprover, setIsApprover] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    const init = async () => {
      const userEmail = (account?.email || account?.username || 'current.user@acom.edu.au').toLowerCase();

      // Load all requests from API
      const allRequests = await getAllRequests();

      // Check if user is named as approver on any request
      const hasApprovalRequests = allRequests.some(req =>
        req.approver?.toLowerCase() === userEmail ||
        (req.approvers && Array.isArray(req.approvers) &&
         req.approvers.some(email => email.toLowerCase() === userEmail))
      );

      // Also check AD group membership (but don't block on it)
      let approverStatus = false;
      try {
        approverStatus = await checkIfUserIsApprover(userEmail, instance, account);
      } catch (e) {
        console.warn('AD group check failed, relying on request-based approver check:', e);
      }

      const userIsApprover = approverStatus || hasApprovalRequests;

      // Determine which tab to show
      const tab = searchParams.get('tab');
      const status = searchParams.get('status');
      let initialTab = 0;

      if (tab === 'approvals' && userIsApprover) {
        initialTab = 2;
        if (status) {
          setStatusFilter(status);
        }
      } else if (tab === 'pending') {
        initialTab = 1;
      }

      // Batch all state updates together so they trigger a single re-render
      setIsApprover(userIsApprover);
      setTabValue(initialTab);
      setRequests(allRequests);

      // Load notifications
      const userNotifications = getNotifications(userEmail);
      setNotifications(userNotifications.slice(0, 10));
      setUnreadCount(getUnreadCount(userEmail));
      cleanupOldNotifications();
    };

    init();
  }, [account, searchParams]);

  useEffect(() => {
    filterRequests();
  }, [requests, searchTerm, statusFilter, typeFilter, tabValue, isApprover]);

  const loadRequests = async () => {
    const allRequests = await getAllRequests();
    setRequests(allRequests);
  };

  const filterRequests = () => {
    const userEmail = (account?.email || account?.username || 'current.user@acom.edu.au').toLowerCase();
    const globalViewers = [
      'amitchell@acom.edu.au',
      'hhildebrandt@acom.edu.au',
      'rblake@acom.edu.au',
      'lgale@acom.edu.au'
    ];
    const isGlobalViewer = globalViewers.includes(userEmail);

    let filtered = [...requests];

    // Helper: check if request belongs to user (submitted by them)
    const isOwnRequest = (r) => r.submittedBy?.toLowerCase() === userEmail;

    // Helper: check if user is the named approver on a request
    const isApproverOnRequest = (req) => {
      if (req.approver?.toLowerCase() === userEmail) return true;
      if (req.approvers && Array.isArray(req.approvers)) {
        return req.approvers.some(e => e.toLowerCase() === userEmail);
      }
      return false;
    };

    // Tab filter
    if (tabValue === 2 && isApprover) {
      // "My Approvals" tab: show requests where user is the named approver
      filtered = filtered.filter(isApproverOnRequest);
    } else if (tabValue === 1) {
      // "Pending" tab
      if (!isGlobalViewer) {
        filtered = filtered.filter(isOwnRequest);
      }
      filtered = filtered.filter(r => r.status === RequestStatus.PENDING);
    } else if (tabValue === 0) {
      // "All" tab: global viewers see everything, others see only their own
      if (!isGlobalViewer) {
        filtered = filtered.filter(isOwnRequest);
      }
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        (r.title || '').toLowerCase().includes(term) ||
        (r.description || '').toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(r => r.type === typeFilter);
    }

    setFilteredRequests(filtered);
  };

  const handleLogout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: '/',
    });
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this request?')) {
      await deleteRequest(id);
      toast.success('Request deleted successfully');
      loadRequests();
    }
  };

  const handleView = (id) => {
    navigate(`/request/${id}`);
  };

  const columns = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 150,
      renderCell: (params) => (
        <Chip label={formTypeNames[params.value]} size="small" />
      ),
    },
    {
      field: 'submittedDate',
      headerName: 'Submitted Date',
      width: 150,
      valueGetter: (value) => format(new Date(value), 'dd/MM/yyyy'),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value.replace('_', ' ').toUpperCase()}
          size="small"
          sx={{
            backgroundColor: statusColors[params.value].bg,
            color: statusColors[params.value].color,
            fontWeight: 'bold',
          }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleView(params.row.id)}
            color="primary"
          >
            <Visibility />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            color="error"
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#0078d4' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/acom-logo.png"
              alt="ACOM Logo"
              style={{ 
                height: '40px', 
                marginRight: '16px',
                objectFit: 'contain',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: 'white'
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Apps Portal
            </Typography>
          </Box>

          <Button
            color="inherit"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>

          <IconButton
            color="inherit"
            sx={{ mr: 2 }}
            onClick={(e) => setShowNotifications(!showNotifications)}
            aria-describedby="notifications-popover"
          >
            <Badge badgeContent={unreadCount} color="error">
              <Notifications />
            </Badge>
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {account?.name || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {account?.username || account?.email || 'user@acom.edu.au'}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#ff6b00' }}>
              {account?.name?.charAt(0) || 'U'}
            </Avatar>
            <IconButton color="inherit" onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
          My Requests
        </Typography>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search requests..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ flexGrow: 1, minWidth: 250 }}
            />
            
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value={RequestStatus.PENDING}>Pending</MenuItem>
              <MenuItem value={RequestStatus.IN_PROGRESS}>In Progress</MenuItem>
              <MenuItem value={RequestStatus.APPROVED}>Approved</MenuItem>
              <MenuItem value={RequestStatus.REJECTED}>Rejected</MenuItem>
              <MenuItem value={RequestStatus.HOLD}>On Hold</MenuItem>
              <MenuItem value={RequestStatus.COMPLETED}>Completed</MenuItem>
            </TextField>

            <TextField
              select
              label="Type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Types</MenuItem>
              {Object.entries(formTypeNames).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setTypeFilter('all');
                setTabValue(0);
              }}
            >
              Clear Filters
            </Button>
          </Box>

          <Tabs value={tabValue} onChange={(e, newValue) => {
            setTabValue(newValue);
          }}>
            <Tab label={`All (${(() => {
              const ue = (account?.email || account?.username || '').toLowerCase();
              const isGV = ['amitchell@acom.edu.au','hhildebrandt@acom.edu.au','rblake@acom.edu.au','lgale@acom.edu.au'].includes(ue);
              if (isGV) return requests.length;
              return requests.filter(r => r.submittedBy?.toLowerCase() === ue).length;
            })()})`} />
            <Tab label={`Pending (${(() => {
              const ue = (account?.email || account?.username || '').toLowerCase();
              const isGV = ['amitchell@acom.edu.au','hhildebrandt@acom.edu.au','rblake@acom.edu.au','lgale@acom.edu.au'].includes(ue);
              let pending = requests.filter(r => r.status === RequestStatus.PENDING);
              if (!isGV) pending = pending.filter(r => r.submittedBy?.toLowerCase() === ue);
              return pending.length;
            })()})`} />
            {isApprover && (
              <Tab
                label={`My Approvals (${(() => {
                  const userEmail = (account?.email || account?.username || '').toLowerCase();
                  return requests.filter(req => {
                    if (req.approver?.toLowerCase() === userEmail) return true;
                    if (req.approvers && Array.isArray(req.approvers)) {
                      return req.approvers.some(e => e.toLowerCase() === userEmail);
                    }
                    return false;
                  }).length;
                })()})`}
                sx={{
                  color: tabValue === 2 ? '#ff6b00' : 'inherit',
                  fontWeight: tabValue === 2 ? 'bold' : 'normal',
                }}
              />
            )}
          </Tabs>
        </Paper>

        {/* Data Grid */}
        <Paper sx={{ height: 600 }}>
          <DataGrid
            rows={filteredRequests}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
              },
            }}
          />
        </Paper>

        {/* Summary */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 2, flexGrow: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total Requests
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {filteredRequests.length}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flexGrow: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Pending Approval
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: statusColors[RequestStatus.PENDING].color }}>
              {filteredRequests.filter(r => r.status === RequestStatus.PENDING).length}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flexGrow: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Approved
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: statusColors[RequestStatus.APPROVED].color }}>
              {filteredRequests.filter(r => r.status === RequestStatus.APPROVED).length}
            </Typography>
          </Paper>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © 2025 ACOM. All rights reserved.
          </Typography>
        </Container>
      </Box>

      {/* Notifications Popover */}
      <Popover
        id="notifications-popover"
        open={showNotifications}
        anchorEl={document.querySelector('[aria-describedby="notifications-popover"]')}
        onClose={() => setShowNotifications(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { width: 400, mt: 1 }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Notifications</Typography>
          <Button
            size="small"
            onClick={() => {
              markAllAsRead(account?.email || account?.username);
              setUnreadCount(0);
              const updatedNotifications = notifications.map(n => ({ ...n, read: true }));
              setNotifications(updatedNotifications);
            }}
          >
            Mark all as read
          </Button>
        </Box>
        <Divider />
        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No notifications</Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {notifications.map((notification) => (
              <React.Fragment key={notification.id}>
                <ListItemButton
                  onClick={() => {
                    markAsRead(notification.id);
                    if (notification.requestId) {
                      navigate(`/request/${notification.requestId}`);
                    }
                    setShowNotifications(false);
                    setUnreadCount(prev => Math.max(0, prev - 1));
                  }}
                  sx={{
                    backgroundColor: notification.read ? 'transparent' : 'action.hover',
                  }}
                >
                  <ListItemIcon>
                    {notification.read ? (
                      <CheckCircle sx={{ color: 'text.secondary' }} />
                    ) : (
                      <Circle sx={{ color: 'primary.main' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={notification.title}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.primary">
                          {notification.message}
                        </Typography>
                        <br />
                        <Typography component="span" variant="caption" color="text.secondary">
                          {format(new Date(notification.createdAt), 'dd MMM yyyy, HH:mm')}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        )}
      </Popover>
    </Box>
  );
};

export default MyRequests;